import { useSelector } from 'react-redux';
import { Combinator, EditableQuery } from 'modules/queryBuilder/types';
import { EditorBaseProps } from 'modules/workflowNodeEditors/types';
import { removeEmptyDeep } from 'modules/workflows/components/NodeEditor/utils';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { FieldMatchNodeData, SearchCombo } from 'modules/workflows/types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Body2Bold, spacing } from 'ui/theme';
import {
  formValidationMessagesForLists,
  getFieldsMatchEditorNodeDataToBeCompared,
  prepareClonedData,
} from 'modules/workflowNodeEditors/components/fieldsMatchEditor/FieldsMatchQueryBuilder.utils';
import TestSplitResultsButton from './TestSplitResultsButton/TestSplitResultsButton';
import { FIELDS_MATCH_TOGGLE_IDS, YES_NO_SPLIT_EDITOR_TEST_ID } from './YesNoSplitEditor.constants';
import { useIsMatchTriggeringEventSelected } from './YesNoSplitEditor.hooks';
import { FormContent } from './YesNoSplitEditor.styled';
import { YesNoSplitEditorFormProvider } from './YesNoSplitFormProvider';
import { YesNoSplitToggle } from './YesNoSplitToggle';
import { FieldsMatchWarningBanner } from '../fieldsMatchEditor/FieldsMatchWarningBanner';
import { isUndeterminableOrigin } from '.';
import {
  getStartNodeTriggerEventName,
  getStartNodeTriggerNodeClass,
  WORKFLOW_TRIGGERS,
} from 'modules/workflows';
import { CloneErrorBanner } from '../CloneErrorBanner/CloneErrorBanner';
import { StyledCloneWarningsHeader } from 'modules/queryBuilder/legacyQueryBuilder/components/QueryBuilder.styled';
import { useWatch } from 'react-hook-form';
import { parseSearchQueryResponseData } from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import { StyledClonedItems } from 'modules/queryBuilder/legacyQueryBuilder/components/QueryBuilder.styled';
import isEmpty from 'lodash/isEmpty';
import { WarningBanner } from 'ui/banner';
import styled from 'styled-components';
import { QueryBuilderForm } from 'modules/queryBuilder/QueryBuilderForm';
import { JourneyEventSelect } from './QueryBuilderOverrides/JourneyEventSelect';
import { JourneyFieldSelect } from './QueryBuilderOverrides/JourneyFieldSelect';
import { JourneyUserPropertySelect } from './QueryBuilderOverrides/JourneyUserPropertySelect';

const StyledCloneWarningContainer = styled.div`
  padding-bottom: ${spacing.base}
`;

export const DEFAULT_YES_NO_SPLIT_QUERY = {
  combinator: Combinator.AND,
  searchQueries: [{ combinator: Combinator.AND, searchQueries: [] }],
};

type CloneWarningMessageType = {
  validationMessageHeader: string;
  validationMessages: {
    location: string;
    items: string[];
  }[];
}[];

export type YesNoSplitEditorProps = EditorBaseProps<FieldMatchNodeData>;

const keysToRemove = ['id'];

const YesNoSplit = ({
  nodeData,
  clonedFromNodeData,
  nodeId,
  node,
  isClonedFromNodeDataLoading,
  ...rest
}: YesNoSplitEditorProps) => {
  const dispatch = useDispatch();
  const [rawQuery, setRawQuery] = useState<EditableQuery>(
    DEFAULT_YES_NO_SPLIT_QUERY as unknown as EditableQuery,
  );
  const isMatchTriggeringEventSelected = useIsMatchTriggeringEventSelected();
  const matchTriggeringEventFields = useWatch({ name: 'matchTriggeringEventFields' });

  const getNodeDataForSaving = useCallback(() => {
    return {
      searchQuery: rawQuery,
      matchTriggeringEventFields: isMatchTriggeringEventSelected,
    };
  }, [isMatchTriggeringEventSelected, rawQuery]);

  /* NodeData needed for Unsaved Changes Modal ----------------------------------- */
  const initialNodeData = useMemo(
    () => getFieldsMatchEditorNodeDataToBeCompared(removeEmptyDeep(nodeData), keysToRemove),
    [nodeData],
  );

  const updatedNodeData = useMemo(() => {
    const data = getNodeDataForSaving();
    return getFieldsMatchEditorNodeDataToBeCompared(removeEmptyDeep(data), keysToRemove);
  }, [getNodeDataForSaving]);

  /* Update Node Editor ----------------------------------- */
  const handleUpdate = useCallback(() => {
    const nodeDataForSaving = getNodeDataForSaving();
    dispatch(updateNodeData(nodeDataForSaving, nodeId));
  }, [dispatch, getNodeDataForSaving, nodeId]);

  /** Warning Banner ----------------------------------- */
  const startNodeClass = useSelector(getStartNodeTriggerNodeClass);
  const startNodeEventName = useSelector(getStartNodeTriggerEventName);

  const startNodeTriggersOnUnspecifiedCustomEvent =
    startNodeClass === WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT &&
    (!startNodeEventName || startNodeEventName.length === 0);
  const isWarningShown =
    matchTriggeringEventFields === FIELDS_MATCH_TOGGLE_IDS.TRIGGERING_EVENT &&
    (isUndeterminableOrigin(startNodeClass) ||
      node.hasError ||
      startNodeTriggersOnUnspecifiedCustomEvent);

  const [cloningValidationMessages, setCloningValidationMessages] =
    useState<CloneWarningMessageType | null>(null);
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  const passedQuery = useMemo(() => {
    if (isClonedFromNodeDataLoading) return null;
    const parsed = parseSearchQueryResponseData(nodeData) as any; // any for now but we will update once we move these utils over.

    // the way to provide init data to the queryBuilder component is by storing things in its redux store...
    // but we only need to store the data into redux, if the node comes in with saved search queries,
    // otherwise we want it to initialize with an empty query

    if (isEmpty(parsed?.searchQueries) && !isEmpty(clonedFromNodeData)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parsedClonedData = parseSearchQueryResponseData(clonedFromNodeData) as any; // any for now but we will update once we move these utils over.

      // which campaigns and lists, etc, are not available to select? warn them
      const validationMessages = formValidationMessagesForLists(parsedClonedData);
      // prep clonedData so that all searchQuery.isClonedData=true so the UI can know.
      // also removes values for all lists, campagins, message channels, messages types, etc so the user can't save a non-existent value
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const preparedClonedData = prepareClonedData(parsedClonedData) as any;
      setCloningValidationMessages(validationMessages);

      const query = {
        searchQueries: preparedClonedData.searchQueries,
        combinator: preparedClonedData.combinator,
      };
      return query;
    } else if (parsed.searchQueries) {
      const query = {
        searchQueries: parsed.searchQueries,
        combinator: parsed.combinator,
      };
      return query;
    } else {
      return DEFAULT_YES_NO_SPLIT_QUERY;
    }
  }, [nodeData, clonedFromNodeData, isClonedFromNodeDataLoading]);

  return (
    <NodeEditor
      {...rest}
      dataTest={YES_NO_SPLIT_EDITOR_TEST_ID}
      node={node}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
      footerButtons={
        rest.workflowData?.workflowType === WorkflowType.DRAFT ? (
          <TestSplitResultsButton
            searchQuery={rawQuery as unknown as SearchCombo}
            node={node}
            nodeId={nodeId}
            workflowData={rest.workflowData}
          />
        ) : null
      }
    >
      <FormContent>
        {isWarningShown && (
          <FieldsMatchWarningBanner
            startNodeClass={startNodeClass}
            startNodeTriggersOnUnspecifiedCustomEvent={startNodeTriggersOnUnspecifiedCustomEvent}
          />
        )}
        <Body2Bold>Filter by</Body2Bold>
        <YesNoSplitToggle />
        <Body2Bold>Split criteria</Body2Bold>
        <>
          {cloningValidationMessages && cloningValidationMessages.length > 0 && (
            <StyledCloneWarningContainer>
              <CloneErrorBanner />
              <StyledCloneWarningContainer>
                {cloningValidationMessages?.map(
                  ({ validationMessageHeader: header, validationMessages: messageObjects }) => {
                    return (
                      <WarningBanner key={header}>
                        <StyledCloneWarningsHeader>{header}</StyledCloneWarningsHeader>
                        {messageObjects.map(({ location, items }) => (
                          <>
                            <div key={location}>{location}</div>
                            <StyledClonedItems key={location}>
                              {items.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </StyledClonedItems>
                          </>
                        ))}
                      </WarningBanner>
                    );
                  },
                )}
              </StyledCloneWarningContainer>
            </StyledCloneWarningContainer>
          )}
        </>

        <div data-test={'yes-no-query-builder'}>
          {!isClonedFromNodeDataLoading && !!passedQuery ? (
            <QueryBuilderForm
              onSetEditableQuery={setRawQuery}
              onSubmitSuccess={() => undefined}
              id={'yes-no-query-builder'}
              isReadOnly={isTileEditorReadOnly}
              query={passedQuery}
              visualComponents={{
                EventSelect: JourneyEventSelect,
                ConditionFieldSelect: JourneyFieldSelect,
                UserProfileSelect: JourneyUserPropertySelect,
              }}
            />
          ) : null}
        </div>
      </FormContent>
    </NodeEditor>
  );
};

export const YesNoSplitEditor = (props: YesNoSplitEditorProps) => {
  return (
    <YesNoSplitEditorFormProvider>
      <YesNoSplit {...props} />
    </YesNoSplitEditorFormProvider>
  );
};
